import type { ModuleId } from '@/modules/catalog';
import type { IconName } from '@/ui/Icon';
import type { Role } from '@/ui/vocabulary';
import { DESTINATIONS } from './destinations';

/** The staff roles a rail is shaped for. The customer role only ever uses an approval link. */
export type RoleId = Exclude<Role, 'CUSTOMER'>;

export type DestinationId =
  | 'today'
  | 'field'
  | 'schedule'
  | 'customers'
  | 'sales'
  | 'finance'
  | 'pricebook'
  | 'reports'
  | 'team'
  | 'settings';

export type ViewId =
  | 'today'
  | 'field'
  | 'my-handoffs'
  | 'board'
  | 'map'
  | 'jobs'
  | 'customers'
  | 'deals'
  | 'leads'
  | 'proposals'
  | 'approvals'
  | 'handoffs'
  | 'installations'
  | 'followups'
  | 'invoices'
  | 'payroll'
  | 'catalogue'
  | 'stock'
  | 'reports'
  | 'team'
  | 'settings';

/** A part of a page that search opens directly, such as one tab of Settings. */
export interface NavPlace {
  /** What it is called in the results, before the name of the destination it is in. */
  readonly label: string;
  /** Where it opens, query and all. */
  readonly to: string;
  /**
   * Every capability needed to open it. Hiding is a courtesy, as it is for a view: the page
   * refuses on its own, and says which one is missing.
   */
  readonly permission?: string | readonly string[];
  /** The release module that ships it, when that is not its page's. */
  readonly module?: ModuleId;
  /** Other words people type when they look for it. */
  readonly keywords?: readonly string[];
}

/** One screen inside a destination: a tab on the destination's view routes. */
export interface NavView {
  /** Stable id, also the suffix of the tab's test id. */
  readonly id: ViewId;
  readonly label: string;
  /**
   * The route prefixes the view owns. The first is where its tab points; every deeper path
   * (a record, an import wizard) maps back to the view by its longest matching prefix.
   */
  readonly paths: readonly [string, ...string[]];
  /**
   * The capability a user needs to see it.
   *
   * Hiding is a courtesy, not a control: the route is guarded independently and the server
   * refuses independently. Undefined means everyone signed in.
   */
  readonly permission?: string;
  /** The release module that ships it. A view of a module that is off is neither listed nor routed. */
  readonly module: ModuleId;
  /** Other words people type when they look for it, such as the name it had before. */
  readonly keywords?: readonly string[];
  /** The parts of it search can open directly, for a page that holds many settings in one. */
  readonly places?: readonly NavPlace[];
}

/** A place in the rail, holding the views that belong together. */
export interface NavDestination {
  /** Stable id, also the suffix of the rail link's test id. */
  readonly id: DestinationId;
  readonly label: string;
  /** The compact glyph for places outside the rail, such as an empty state. */
  readonly icon: IconName;
  /** Its views in tab order. The first one a person may open is where the rail link goes. */
  readonly views: readonly [NavView, ...NavView[]];
  /** The roles whose rail carries it. A person holding several roles gets the union. */
  readonly pinnedFor: readonly RoleId[];
}

/** What decides whether a view is offered: the capabilities held and the release modules shipped. */
export interface ViewAccess {
  readonly can: (permission: string) => boolean;
  readonly enabled: (module: ModuleId) => boolean;
}

/** What shapes a rail: the roles held, on top of what decides each view. */
export interface NavAccess extends ViewAccess {
  readonly roles: readonly string[];
}

/** A rail link: the destination and the page it opens for this person. */
export interface RailEntry {
  readonly destination: NavDestination;
  readonly path: string;
}

/** A view together with the destination that holds it. */
export interface PageRef {
  readonly destination: NavDestination;
  readonly view: NavView;
}

/** Where a path sits in the registry. */
export interface NavLocation extends PageRef {
  /** True on the view's own page; false on a record or tool page beneath it. */
  readonly isViewRoute: boolean;
}

export { DESTINATIONS };

/** Every view, in registry order. */
export const VIEWS: readonly NavView[] = DESTINATIONS.flatMap((destination) => destination.views);

function isOffered(view: NavView, { can, enabled }: ViewAccess): boolean {
  return enabled(view.module) && (view.permission === undefined || can(view.permission));
}

/**
 * The views of a destination this person may open, in tab order.
 *
 * @param destination the destination
 * @param access what the person holds and what this installation ships
 * @returns the views that are both permitted and module-enabled
 */
export function offeredViews(destination: NavDestination, access: ViewAccess): readonly NavView[] {
  return destination.views.filter((view) => isOffered(view, access));
}

/**
 * The rail for one person: the destinations their roles pin that have something they can open.
 *
 * @param access what the person holds and what this installation ships
 * @returns the entries in rail order, each linking to the destination's first offered view
 */
export function railFor(access: NavAccess): readonly RailEntry[] {
  return DESTINATIONS.flatMap((destination) => {
    const pinned = destination.pinnedFor.some((role) => access.roles.includes(role));
    const [first] = pinned ? offeredViews(destination, access) : [];
    return first === undefined ? [] : [{ destination, path: first.paths[0] }];
  });
}

/**
 * Every page this person may open, pinned to their rail or not.
 *
 * The rail is shaped by role; this is what search draws on, so nothing a person may open is
 * hidden from them, only quieter.
 *
 * @param access what the person holds and what this installation ships
 * @returns the offered views with their destinations, in registry order
 */
export function offeredPages(access: ViewAccess): readonly PageRef[] {
  return DESTINATIONS.flatMap((destination) =>
    offeredViews(destination, access).map((view) => ({ destination, view })),
  );
}

/** A part of a page together with the page and destination that hold it. */
export interface PlaceRef extends PageRef {
  readonly place: NavPlace;
}

function capabilitiesOf(place: NavPlace): readonly string[] {
  if (place.permission === undefined) return [];
  return typeof place.permission === 'string' ? [place.permission] : place.permission;
}

/**
 * Every part of a page this person may open, for search to offer by its own name.
 *
 * @param access what the person holds and what this installation ships
 * @returns the places of the pages that are offered, in registry order
 */
export function offeredPlaces(access: ViewAccess): readonly PlaceRef[] {
  return offeredPages(access).flatMap(({ destination, view }) =>
    (view.places ?? [])
      .filter(
        (place) =>
          (place.module === undefined || access.enabled(place.module)) &&
          capabilitiesOf(place).every((capability) => access.can(capability)),
      )
      .map((place) => ({ destination, view, place })),
  );
}

interface PrefixOwner extends PageRef {
  readonly prefix: string;
}

/** Longest prefix first, so `/proposals/approvals` is claimed before `/proposals`. */
const PREFIXES: readonly PrefixOwner[] = DESTINATIONS.flatMap((destination) =>
  destination.views.flatMap((view) => view.paths.map((prefix) => ({ destination, view, prefix }))),
).sort((a, b) => b.prefix.length - a.prefix.length);

function withoutTrailingSlash(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

/**
 * Where a path sits in the registry.
 *
 * Longest prefix wins, on whole path segments, so `/jobs/4821` belongs to Jobs rather than to
 * Today (whose path is a prefix of everything) and `/replacement-handoff` is not mistaken for
 * `/replacement`.
 *
 * @param pathname the current location
 * @returns the destination and view the path belongs to, or undefined for a path nobody owns
 */
export function locatePath(pathname: string): NavLocation | undefined {
  const path = withoutTrailingSlash(pathname);
  const owner = PREFIXES.find(
    ({ prefix }) => path === prefix || (prefix !== '/' && path.startsWith(`${prefix}/`)),
  );
  if (owner === undefined) return undefined;
  return { destination: owner.destination, view: owner.view, isViewRoute: path === owner.prefix };
}

/**
 * The name a page goes by in the browser title, the assistant's header and search results.
 *
 * @param page the view and the destination that holds it
 * @returns the view and its destination when the destination has several views, else its name
 */
export function pageName({ destination, view }: PageRef): string {
  return destination.views.length > 1 ? `${view.label} · ${destination.label}` : destination.label;
}
